import { Link } from 'react-router-dom';
import { ArrowDownIcon, ArrowUpIcon, MoonIcon, SunIcon, XIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { Field } from '../ui/Field';
import { NumberInput } from '../ui/NumberInput';
import { useAdmin } from '../AdminContext';
import { btnIcon, inputCls } from '../ui/styles';
import { refillLabel, refillState } from '../../utils/account';
import type { PlanItem, RoutineSlot } from '../../core/types/account';
const slotMeta: {
  key: RoutineSlot;
  label: string;
  icon: typeof SunIcon;
}[] = [{
  key: 'ochtend',
  label: 'Ochtend',
  icon: SunIcon
}, {
  key: 'avond',
  label: 'Avond',
  icon: MoonIcon
}];
type Props = {
  item: PlanItem;
  step: number;
  isFirst: boolean;
  isLast: boolean;
  onChange: (patch: Partial<PlanItem>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
};

/** Eén product in het huidplan: volgorde, moment van de dag, instructie en hoe lang het meegaat. */
export function PlanItemRow({
  item,
  step,
  isFirst,
  isLast,
  onChange,
  onMove,
  onRemove
}: Props) {
  const {
    index
  } = useAdmin();
  const product = index.productById(item.productId);
  const productName = product?.name ?? 'Product';
  const refill = refillState(item);
  const toggleSlot = (slot: RoutineSlot) => {
    const on = item.slots.includes(slot);
    if (on && item.slots.length === 1) return; // minstens één moment
    onChange({
      slots: on ? item.slots.filter(s => s !== slot) : [...item.slots, slot]
    });
  };
  return <li className="px-4 py-4 sm:px-5" data-id="element-1477" data-mp="2jyicgk7ikvr5">
      <div className="flex items-center gap-3" data-id="element-1478" data-mp="2f0p4blmnhro8">
        <span className="w-5 shrink-0 text-center font-display text-[17px] text-clay" aria-hidden="true" data-id="element-1479" data-mp="2ezxk16yi7gd9">
          {step}
        </span>
        {product ? <ProductImage src={product.image} className="h-11 w-11 shrink-0 rounded p-0.5" data-id="element-1480" data-mp="2osuvit52w52d" /> : <span className="h-11 w-11 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-1481" data-mp="32s47thq5yf12" />}
        <span className="min-w-0 flex-1" data-id="element-1482" data-mp="2xtkmnjje979r">
          {product ? <Link to={`/be/producten/${product.id}`} className="block truncate text-[14px] text-ink hover:text-clay" data-id="element-1483" data-mp="1vfdd0n2adpsd">
              <span className="sr-only" data-id="element-1484" data-mp="1zhgpnf0oz0f8">Stap {step}: </span>
              {product.name}
            </Link> : <span className="block truncate text-[14px] text-clay" data-id="element-1485" data-mp="24dyt8bkacspz">Onbekend product: {item.productId}</span>}
          <span className={`block text-[12px] ${refill.level === 'ruim' ? 'text-muted' : 'text-clay'}`} data-id="element-1486" data-mp="298be8q1euc3u">
            {product ? `${product.step ?? product.category} · ` : ''}
            {refillLabel(refill)}
          </span>
        </span>
        <span className="flex shrink-0" data-id="element-1487" data-mp="2n5hvgg6hk2qy">
          <button type="button" onClick={() => onMove(-1)} disabled={isFirst} aria-label={`${productName} eerder in de routine`} className={btnIcon} data-id="element-1488" data-mp="2n6j8gq72azbb">
            <ArrowUpIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1489" data-mp="ssi7nzryc28l" />
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={isLast} aria-label={`${productName} later in de routine`} className={btnIcon} data-id="element-1490" data-mp="xqjsap5109m6">
            <ArrowDownIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1491" data-mp="q8vdummqxvvi" />
          </button>
          <button type="button" onClick={onRemove} aria-label={`${productName} uit het plan halen`} className={btnIcon} data-id="element-1492" data-mp="ler5q01qvhsu">
            <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1493" data-mp="bixt6go0zob2" />
          </button>
        </span>
      </div>

      <div className="mt-3 grid gap-3 sm:ml-8 sm:grid-cols-2" data-id="element-1494" data-mp="196ogdntgk3sd">
        <div className="flex flex-wrap gap-1.5 sm:col-span-2" role="group" aria-label={`Moment van de dag voor ${productName}`} data-id="element-1495" data-mp="147bsr0urisoq">
          {slotMeta.map(s => {
          const on = item.slots.includes(s.key);
          return <button key={s.key} type="button" aria-pressed={on} onClick={() => toggleSlot(s.key)} className={`inline-flex min-h-10 items-center gap-1.5 rounded-full border px-4 py-2 text-[14px] sm:min-h-0 sm:px-3 sm:py-1.5 sm:text-[13px] transition-colors duration-150 ease-soft ${on ? 'border-ink bg-ink text-shell' : 'border-line bg-white text-muted hover:border-ink/40 hover:text-ink'}`} data-id="element-1496" data-mp="v7f0yqd2tdpg">
                <s.icon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1497" data-mp="xrl4zq4z7nzo" />
                {s.label}
              </button>;
        })}
        </div>
        <label className="block sm:col-span-2" data-id="element-1498" data-mp="1qfpbuezaa73y">
          <span className="sr-only" data-id="element-1499" data-mp="1qcureng9smaj">Instructie voor {productName}</span>
          <textarea value={item.instruction} onChange={e => onChange({
          instruction: e.target.value
        })} rows={2} placeholder="Hoe ze het gebruikt, in jouw woorden" className={`${inputCls} resize-y leading-relaxed`} data-id="element-1500" data-mp="1vd8pbco5qfuu" />
        </label>
        <Field label="Hoe vaak" hint="Leeg = elke keer." data-id="element-1501" data-mp="5x9c9khf469x">
          <input value={item.frequency ?? ''} onChange={e => onChange({
          frequency: e.target.value || undefined
        })} className={inputCls} data-id="element-1502" data-mp="a0xju361v1v6" />
        </Field>
        {/* min-w-0: een native datumveld heeft op iOS een eigen minimumbreedte en duwt anders de kolom eruit. */}
        <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0" data-id="element-1503" data-mp="3tpkd2fnacn4t">
          <Field label="In gebruik sinds" data-id="element-1504" data-mp="cekjqhv9ntbq">
            <input type="date" value={item.startedOn} onChange={e => onChange({
            startedOn: e.target.value
          })} className={`${inputCls} min-w-0`} data-id="element-1505" data-mp="7fzrb4m9adxu" />
          </Field>
          <Field label="Gaat mee (weken)" data-id="element-1506" data-mp="3fw2ouhjnmm8g">
            <NumberInput value={item.lastsWeeks} onChange={v => v !== undefined && onChange({
            lastsWeeks: v
          })} min={1} max={104} data-id="element-1507" data-mp="3awyevoowokh2" />
          </Field>
        </div>
      </div>
    </li>;
}